// Demo data and helpers for the Finances module (dashboard, reconciliation, transactions, reports).
// Generated deterministically over the last 30 days so the screens stay realistic whatever the date.

export type FinModule = "Billets bus" | "Expéditions internationales" | "Livraison urbaine";
export type FinChannel = "Orange Money" | "MTN MoMo" | "Touch / ITX" | "Espèces" | "Virement bancaire";
export type FinType = "Encaissement" | "Remboursement" | "Reversement CR";
export type FinStatus = "Réussie" | "En attente" | "Échouée";

export interface Transaction {
  id: string;
  ref: string;
  date: string; // ISO datetime
  module: FinModule;
  dossier: string;
  client: string;
  channel: FinChannel;
  opRef: string; // operator reference ("" for cash)
  type: FinType;
  amount: number; // signed: negative for refunds / payouts
  fee: number; // operator fee
  status: FinStatus;
  agence: string;
  agent: string;
}

export type StatementSource = "Orange Money" | "MTN MoMo" | "Touch / ITX" | "Banque";

export interface StatementLine {
  id: string;
  source: StatementSource;
  date: string;
  opRef: string;
  amount: number;
  label: string;
}

export const MODULES: FinModule[] = ["Billets bus", "Expéditions internationales", "Livraison urbaine"];
export const CHANNELS: FinChannel[] = ["Orange Money", "MTN MoMo", "Touch / ITX", "Espèces", "Virement bancaire"];
export const AGENCES = ["Agence AKWA", "Agence DEIDO", "Agence MVAN", "Agence Mvog-Mbi", "Hub Garoua"];
export const STATEMENT_SOURCES: StatementSource[] = ["Orange Money", "MTN MoMo", "Touch / ITX", "Banque"];

export const money = (n: number) =>
  (n < 0 ? "−" : "") +
  Math.round(Math.abs(n))
    .toLocaleString("fr-FR")
    .replace(/[  ,]/g, " ") +
  " FCFA";

const pad = (n: number) => String(n).padStart(2, "0");
export const isoDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const isoDateTime = (d: Date) => `${isoDate(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
export const todayIso = () => isoDate(new Date());
export const addDaysIso = (iso: string, n: number) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return isoDate(d);
};
export const fDay = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
export const fDateTime = (iso: string) => new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export const channelToSource = (c: FinChannel): StatementSource | null =>
  c === "Espèces" ? null : c === "Virement bancaire" ? "Banque" : (c as StatementSource);

export const feeFor = (c: FinChannel, amount: number) =>
  c === "Espèces" ? 0 : c === "Virement bancaire" ? 500 : Math.round(Math.abs(amount) * (c === "Touch / ITX" ? 0.015 : 0.01));

/* ---------------------------------- Seed ---------------------------------- */

// Small deterministic PRNG.
function rng(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

const CLIENTS = ["NGONO BRIGITTE", "KAMGA PAUL", "ABENA MARC", "MOFFO ARIELLE", "ETONDE Grace", "Tanga Alexandre", "Boutique Mama Ngo", "Pharmacie du Littoral", "Etoundi Serge", "Ngo Bakot Julienne", "TCHOUPO ARNAUD", "Kedi ba Mbok", "Bella Cosmetics", "Hôtel Ilomba", "NDZANA SANDRINE"];
const AGENTS = ["yasmine", "BAPPA", "Mireille N.", "Kevin M.", "Ange T."];

export function generateFinanceData() {
  const rand = rng(424242);
  const pick = <T,>(a: T[]) => a[Math.floor(rand() * a.length)];
  const now = new Date();
  const txs: Transaction[] = [];
  let n = 0;
  for (let dayOffset = 29; dayOffset >= 0; dayOffset--) {
    const perDay = 3 + Math.floor(rand() * 5) + (dayOffset % 7 === 1 ? 3 : 0);
    for (let i = 0; i < perDay; i++) {
      n++;
      const d = new Date(now);
      d.setDate(d.getDate() - dayOffset);
      d.setHours(7 + Math.floor(rand() * 13), Math.floor(rand() * 60), 0, 0);
      if (d > now) d.setTime(now.getTime() - (i + 1) * 600000);
      const module = rand() < 0.5 ? "Billets bus" : rand() < 0.55 ? "Expéditions internationales" : "Livraison urbaine";
      const r = rand();
      const channel: FinChannel = r < 0.42 ? "Orange Money" : r < 0.7 ? "MTN MoMo" : r < 0.8 ? "Touch / ITX" : r < 0.94 ? "Espèces" : "Virement bancaire";
      const base = module === "Billets bus" ? [3000, 3500, 5000, 5500, 6000, 10000][Math.floor(rand() * 6)] : module === "Expéditions internationales" ? 45000 + Math.floor(rand() * 40) * 10000 : 1500 + Math.floor(rand() * 8) * 500;
      const tr = rand();
      const type: FinType = tr < 0.9 ? "Encaissement" : tr < 0.96 ? "Remboursement" : "Reversement CR";
      const amount = type === "Encaissement" ? base : type === "Remboursement" ? -base : -(15000 + Math.floor(rand() * 10) * 5000);
      const statusR = rand();
      const status: FinStatus = dayOffset <= 1 && statusR < 0.15 ? "En attente" : statusR < 0.04 ? "Échouée" : "Réussie";
      const day = `${pad(d.getDate())}${pad(d.getMonth() + 1)}`;
      const opPrefix = channel === "Orange Money" ? "MP" : channel === "MTN MoMo" ? "MM" : channel === "Touch / ITX" ? "ITX-" : channel === "Virement bancaire" ? "VIR" : "";
      const opRef = opPrefix ? `${opPrefix}${channel === "Touch / ITX" ? "" : day}${String(Math.floor(rand() * 900000) + 100000)}` : "";
      const dossier =
        module === "Billets bus" ? `TIK${Math.floor(rand() * 0xffffffff).toString(16).toUpperCase().padStart(8, "0")}` : module === "Expéditions internationales" ? `EXP-${day}-${pick(["FR", "BE", "CA", "US", "DE"])}-0${100 + (n % 900)}` : `LIV-${day}-${pick(["DLA", "YDE", "GOU"])}-0${100 + (n % 900)}`;
      txs.push({
        id: `t${n}`,
        ref: `TRX-${day}-${String(n).padStart(4, "0")}`,
        date: isoDateTime(d),
        module,
        dossier,
        client: pick(CLIENTS),
        channel,
        opRef,
        type,
        amount,
        fee: status === "Échouée" ? 0 : feeFor(channel, amount),
        status,
        agence: pick(AGENCES),
        agent: pick(AGENTS),
      });
    }
  }

  // Operator statements: one line per successful electronic transaction, with a few anomalies.
  const statement: StatementLine[] = [];
  const links: Record<string, string> = {}; // statementId -> transactionId (already reconciled)
  let s = 0;
  txs.forEach((t, idx) => {
    const source = channelToSource(t.channel);
    if (!source || t.status !== "Réussie") return;
    if (idx % 23 === 5) return; // missing from the operator statement
    s++;
    const amountGap = idx % 29 === 7 ? (t.amount > 0 ? -500 : 500) : 0; // amount mismatch
    const line: StatementLine = { id: `s${s}`, source, date: t.date, opRef: t.opRef, amount: t.amount + amountGap, label: `${t.amount > 0 ? "Paiement reçu" : "Paiement émis"} ${t.client}` };
    statement.push(line);
    // Older, clean lines are already reconciled.
    const ageDays = (Date.now() - new Date(t.date).getTime()) / 86400000;
    if (!amountGap && ageDays > 6) links[line.id] = t.id;
  });
  // Unidentified incoming payments (no matching transaction).
  for (let k = 0; k < 4; k++) {
    s++;
    const d = new Date();
    d.setDate(d.getDate() - (k * 3 + 1));
    d.setHours(9 + k * 2, 15);
    statement.push({ id: `s${s}`, source: (["Orange Money", "MTN MoMo", "Orange Money", "Banque"] as StatementSource[])[k], date: isoDateTime(d), opRef: `${k === 3 ? "VIR" : k === 1 ? "MM" : "MP"}${pad(d.getDate())}${pad(d.getMonth() + 1)}${900100 + k}`, amount: [6000, 3000, 12500, 250000][k], label: ["Paiement reçu 237699XXXXXX", "Paiement reçu 237677XXXXXX", "Paiement reçu 237690XXXXXX", "Virement SARL NDOUMBE LOGISTIQUE"][k] });
  }
  return { txs: txs.reverse(), statement: statement.reverse(), links };
}

/* -------------------------------- Periods --------------------------------- */

export type PeriodKey = "today" | "7d" | "month" | "lastMonth" | "30d" | "custom";
export const PERIOD_LABELS: Record<PeriodKey, string> = {
  today: "Aujourd'hui",
  "7d": "7 derniers jours",
  "30d": "30 derniers jours",
  month: "Ce mois-ci",
  lastMonth: "Mois dernier",
  custom: "Personnalisée",
};

export function periodRange(p: PeriodKey, from?: string, to?: string): [string, string] {
  const t = todayIso();
  const d = new Date();
  switch (p) {
    case "today":
      return [t, t];
    case "7d":
      return [addDaysIso(t, -6), t];
    case "30d":
      return [addDaysIso(t, -29), t];
    case "month":
      return [`${t.slice(0, 7)}-01`, t];
    case "lastMonth": {
      const first = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      const last = new Date(d.getFullYear(), d.getMonth(), 0);
      return [isoDate(first), isoDate(last)];
    }
    default:
      return [from || addDaysIso(t, -29), to || t];
  }
}

export const inRange = (iso: string, [a, b]: [string, string]) => iso.slice(0, 10) >= a && iso.slice(0, 10) <= b;

// Downloads rows as a CSV file (semicolon separated, UTF-8 BOM so Excel opens accents correctly).
export function downloadCsv(filename: string, header: string[], rows: (string | number)[][]) {
  const esc = (v: string | number) => {
    const s = String(v);
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = "﻿" + [header, ...rows].map((r) => r.map(esc).join(";")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
